(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
    (global['konga-graphql-client'] = factory());
}(this, (function () { 'use strict';

    const superagent = require("superagent");

    class GraphQLError extends Error {
        constructor(message, errorBody) {
            super(message);
            this.name = "GraphQLError";

            if (typeof Error.captureStackTrace === "function") {
                Error.captureStackTrace(this, this.constructor);
            } else {
                this.stack = Error().stack;
            }

            this.rawError = errorBody.errors ? errorBody.errors : errorBody;
            this.rawData = errorBody.data;
        }
    }

    const customErrorFactory = (msg, errorBody) => {
        if (!msg) {
            const hasMessage =
                errorBody &&
                Array.isArray(errorBody.errors) &&
                errorBody.errors.length > 0;
            msg =
                hasMessage && errorBody.errors[0].message
                    ? errorBody.errors[0].message
                    : "An unknown error occurred";
        }

        return new GraphQLError(msg, errorBody);
    };

    //Fallback Metrics implementation
    const _metrics = logger => ({
        increment: metric => logger.info(metric)
    });

    /**
     * wrapper around super agent http library with timeout, retry and max attempts options
     * to support callbacks and promises.
     */
    class Transport {
        constructor(logger, metrics) {
            this.requestHelper = superagent;
            this.logger = logger;

            // fallback to "console" for logging if a logger was not provided
            if (!logger) {
                this.logger = console;
            }

            this.metrics = metrics;

            //provide fallback for metric if it isnt supplied
            if (!metrics) {
                this.metrics = _metrics(this.logger);
            }
        }

        /**
         * http handler for transport
         * @param url
         * @param optns
         * @param cb
         * @returns {*}
         */
        send(url, optns, cb) {
            let defaults = {
                method: "POST"
            };

            let isCallback = typeof cb === "function";

            let _optns = Object.assign(defaults, optns);

            let request = this.requestHelper(_optns.method, url)
                .retry(_optns.maxAttempts)
                .timeout(_optns.timeout);

            if (_optns._singleRequestHeaders) {
                this._setHeaders(request, _optns._singleRequestHeaders);
            }

            //Save Time before Request is made
            let startTime = new Date().getTime();

            return request
                .send(_optns.body)
                .then(response => {
                    let elapsedTime = new Date().getTime() - startTime;

                    this.metrics.increment("graphql_client.request.ok");

                    this.logger.info(
                        `request completed with a success and took ${elapsedTime}ms to finish`
                    );

                    let data = response.body;

                    //konga graphql api endpoint returns most results with a data wrapper
                    if (response.body && response.body.data) {
                        data = response.body.data;
                    }

                    //handle situations where response contains error
                    if (response.body && response.body.errors) {
                        if (isCallback) {
                            return cb(customErrorFactory(null, response.body));
                        }

                        throw customErrorFactory(null, response.body);
                    }

                    if (isCallback) {
                        return cb(null, data);
                    }

                    return data;
                })
                .catch(err => {
                    if (err.timeout) {
                        this.metrics.increment(
                            "graphql_client.request.error.timeout"
                        );
                        this.logger.error(
                            `request failed because timeout of ${
                            err.timeout
                        }ms was exceeded ` + `before a response was returned`
                        );
                    } else {
                        this.metrics.increment("graphql_client.request.error");

                        if (err.message) {
                            this.logger.error(
                                `request errored with reason ${err.message}`
                            );
                        } else {
                            this.logger.error("request errored out %O: ", err);
                        }
                    }

                    if (isCallback) {
                        return cb(this.parseErrors(err), null);
                    }

                    throw this.parseErrors(err);
                });
        }

        /**
         * Sets header on request to be sent
         * @param request
         * @param headers
         * @returns {*}
         * @private
         */
        _setHeaders(request, headers) {
            Object.keys(headers).forEach(item => {
                request.set(item, headers[item]);
            });
        }

        parseErrors(err) {
            if (!err.response || !err.response.body) {
                return err;
            }

            return customErrorFactory(err.message, err.response.body);
        }
    }

    var transport = (logger, metrics) => new Transport(logger, metrics);

    class Client {
        constructor(url, optns) {
            if (!url || typeof url !== "string") {
                throw new Error("a request url must be specified!");
            }

            this.url = url;

            this.optns = {
                //setup default headers
                headers: {
                    "content-type": "application/json"
                }
            };

            Object.assign(this.optns, {
                maxAttempts: optns.maxAttempts, // Specifies the maximum number of attempts
                retryDelay: optns.retryDelay, // Specifies the time in ms to wait before trying again
                timeout: optns.timeout // duration before assuming request is a failed attempt
            });

            //initialize http transport with logger and metrics
            this.transport = transport(optns.logger, optns.metrics);
        }

        /**
         * @description builds the options object for the current request
         * @param optns config options per request specified by the user
         * @returns {*}
         * @private
         */
        _setRequestOptions(optns) {
            this.setRequestHeaders(optns.headers);

            //So the headers for this request does not override the default headers
            delete optns.headers;

            let _optns = Object.assign({}, this.optns, optns);

            return _optns;
        }

        /**
         * @description generates the headers to be used for each request,
         * also allows users to specify extra headers only for the current request
         * @param requestHeaders headers to be used for this singular request
         * @returns {Client}
         */
        setRequestHeaders(requestHeaders = {}) {
            this.optns._singleRequestHeaders = Object.assign({},
                this.optns.headers,
                requestHeaders
            );
            return this;
        }

        /**
         * Resets the single request headers values to an empty object
         * @private
         */
        _resetRequestHeaders() {
            this.optns._singleRequestHeaders = {};
        }

        /**
         * @description validates the arguments for every request
         * @param query
         * @param opts
         * @private
         */
        _validateRequest(query, opts) {
            if (!query) {
                throw new Error("query is required!");
            }

            if (typeof opts === "function") {
                throw new Error(
                    "when using callback and the options object is empty set it to either null or {}"
                );
            }
        }

        /**
         * @description method to query a graphql endpoint
         * @param query
         * @param opts
         * @param cb
         * @return {*}
         */
        query(query, opts, cb) {
            this._validateRequest(query, opts);

            let data = {
                query: query
            };

            let config = opts && opts.config ? opts.config : {};

            //if _singleRequestHeaders exist its because the user chained query to a setRequestHeaders method
            if (this.optns._singleRequestHeaders) {
                config.headers = Object.assign({},
                    this.optns._singleRequestHeaders,
                    config.headers
                );
            }

            let _optns = this._setRequestOptions(config);

            if (_optns.variables) {
                data.variables = _optns.variables;
            }

            _optns.body = JSON.stringify(data);

            let result = this.transport.send(this.url, _optns, cb);

            //reset request headers object for the next request
            this._resetRequestHeaders();

            return result;
        }

        /**
         * @description method to perform a mutation on a graphql endpoint
         * @param query
         * @param opts
         * @param cb
         * @return {*}
         */
        mutate(query, opts, cb) {
            this._validateRequest(query, opts);

            let config = opts && opts.config ? opts.config : {};

            //if _singleRequestHeaders exist its because the user chained query to a setRequestHeaders method
            if (this.optns._singleRequestHeaders) {
                config.headers = Object.assign({},
                    this.optns._singleRequestHeaders,
                    config.headers
                );
            }

            let _optns = this._setRequestOptions(config);

            let data = {
                query: "mutation" + query
            };

            if (_optns.variables) {
                data.variables = _optns.variables;
            }

            _optns.body = JSON.stringify(data);

            let result = this.transport.send(this.url, _optns, cb);

            //reset request headers object for the next request
            this._resetRequestHeaders();

            return result;
        }
    }

    return Client;

})));
